import React, { useState, useEffect } from 'react'
import { Popup } from 'semantic-ui-react'
import { filters, FilterSchema } from '@adc/blueprint'
import FilterButton from './filterButton'
import FilterPanel from './filterPanel'
import FilterGroupLayout from './filterGroupLayout'
import classnames from 'classnames/bind'
import styles from './styles.scss'
import type { ButtonWidth, PanelHeight } from './types'
const cx = classnames.bind(styles)

type FilterProps = {
  defaultButtonLabel?: string // optional hard-coded label
  definition: FilterSchema.FilterGroupDefinition
  data: FilterSchema.FilterGroupViewModel
  buttonWidth?: ButtonWidth
  panelHeight?: PanelHeight
  onChange: (key: string, value: FilterSchema.ValueType) => void
  onReset: () => void
  elmId?: string
}

/**
 * Filter: a filter component that renders one filter group (e.g. Property filter).
 *
 * @definition group definition
 * @data group data
 *
 * @returns
 */
const Filter: React.FunctionComponent<FilterProps> = ({
  defaultButtonLabel,
  definition,
  data,
  buttonWidth,
  panelHeight,
  onChange,
  onReset,
  elmId = 'filter',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isFilterSelected, setIsFilterSelected] = useState(false) // as in, at least one filter is in use
  const [filterNotSelectedButtonLabel, setFilterNotSelectedButtonLabel] =
    useState(defaultButtonLabel || definition.label)

  const [buttonLabel, setButtonLabel] = useState(filterNotSelectedButtonLabel)

  useEffect(() => {
    setIsFilterSelected(!filters.utils.isGroupFilterReset(data))
    setButtonLabel(filterNotSelectedButtonLabel)
  }, [data, filterNotSelectedButtonLabel])

  useEffect(() => {
    setFilterNotSelectedButtonLabel(defaultButtonLabel || definition.label)
  }, [defaultButtonLabel, definition])

  useEffect(() => {
    setButtonLabel(filters.utils.getLabelFromGroup(data, definition))
  }, [data, definition])

  return (
    <Popup
      content={
        <FilterPanel
          elmId={`${elmId}_panel`}
          onOpen={setIsOpen}
          onReset={onReset}
        >
          <FilterGroupLayout
            key={definition.name}
            groupDefinition={definition}
            groupData={data}
            onChange={onChange}
            height={panelHeight}
          />
        </FilterPanel>
      }
      on='click'
      open={isOpen}
      onClose={() => setIsOpen(false)}
      onOpen={() => setIsOpen(true)}
      className={cx('filter-popup')}
      trigger={
        <FilterButton
          isFilterSelected={isFilterSelected}
          onOpen={setIsOpen}
          isOpen={isOpen}
          onReset={() => onReset()}
          label={buttonLabel}
          width={buttonWidth}
          elmId={`${elmId}_button`}
        />
      }
      position='bottom left'
    />
  )
}

export default Filter
